<script setup>
/* global axios */
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useAgentsList } from 'dashboard/composables/useAgentsList';
import MultiselectDropdown from 'shared/components/ui/MultiselectDropdown.vue';
import { OwnershipController, eligibleOwnershipAgents, ownershipUrl, ownershipSuccessKind } from './ownershipState.mjs';

const props = defineProps({
  accountId: { type: [Number, String], required: true },
  conversationId: { type: [Number, String], required: true },
});
const emit = defineEmits(['supported']);
const store = useStore();
const { t } = useI18n();
const { agentsList } = useAgentsList(false);
const currentChat = computed(() => store.getters.getSelectedChat);
const currentUser = computed(() => store.getters.getCurrentUser);
const view = ref({ record: null, loading: false, busy: false, error: null, retryable: false });
const choosingAgent = ref(false);
const selectedAgent = ref(null);
const agentsLoading = ref(false);
const agentsError = ref(false);
let agentGeneration = 0;
const key = name => `CONVERSATION.MEGATEXT_OWNERSHIP.${name}`;

const controller = new OwnershipController({
  api: {
    get: async scope => (await axios.get(ownershipUrl(scope))).data,
    post: async (scope, body) => (await axios.post(ownershipUrl(scope), body)).data,
    assign: async (scope, agentId) => axios.post(
      `/api/v1/accounts/${scope.accountId}/conversations/${scope.conversationId}/assignments`,
      { assignee_id: agentId, assignee_type: 'User' }
    ),
  },
  onChange: state => {
    view.value = state;
    emit('supported', state.record?.supported === true);
  },
  onSuccess: (action, scope, record, targetId) => {
    choosingAgent.value = false;
    selectedAgent.value = null;
    store.dispatch('getConversation', scope.conversationId);
    useAlert(t(key(ownershipSuccessKind(action, record, currentUser.value?.id, targetId))));
  },
});

const supported = computed(() => view.value.record?.supported === true);
const disabled = computed(() => view.value.busy || view.value.loading || view.value.retryable);
const eligibleAgents = computed(() => eligibleOwnershipAgents(
  agentsList.value, view.value.record, currentUser.value?.id
));
const statusText = computed(() => {
  const record = view.value.record;
  if (record?.state === 'alexey') return t(key('ALEXEY'));
  if (record?.state === 'waiting') return t(key('WAITING'));
  if (record?.state === 'closed') return t(key('CLOSED'));
  if (record?.assignee_id === currentUser.value?.id) return t(key('YOU'));
  const agent = agentsList.value.find(item => item.id === record?.assignee_id);
  return agent ? t(key('STAFF_NAMED'), { name: agent.name }) : t(key('STAFF'));
});
const errorText = computed(() => view.value.error ? t(key(`ERROR_${view.value.error.toUpperCase()}`)) : '');
const assignmentAllowed = computed(() => eligibleAgents.value.length > 0);

watch(() => [props.accountId, props.conversationId], () => {
  agentGeneration += 1;
  choosingAgent.value = false;
  selectedAgent.value = null;
  agentsError.value = false;
  agentsLoading.value = false;
  controller.setContext(props.accountId, props.conversationId);
}, { immediate: true });

watch(() => [
  currentChat.value?.status,
  currentChat.value?.meta?.assignee?.id,
  currentChat.value?.meta?.assignee_type,
], () => {
  if (supported.value && !view.value.loading) controller.refresh();
});

const openAgentChooser = async () => {
  if (disabled.value) return;
  choosingAgent.value = !choosingAgent.value;
  selectedAgent.value = null;
  if (!choosingAgent.value) return;
  const generation = ++agentGeneration;
  const inboxId = currentChat.value?.inbox_id;
  if (!inboxId) return;
  agentsLoading.value = true;
  agentsError.value = false;
  try {
    await store.dispatch('inboxAssignableAgents/fetch', [inboxId]);
  } catch {
    if (generation === agentGeneration) agentsError.value = true;
  } finally {
    if (generation === agentGeneration) agentsLoading.value = false;
  }
};
const chooseAgent = agent => {
  selectedAgent.value = eligibleAgents.value.find(item => item.id === agent.id) || null;
};
const assignSelected = () => {
  if (selectedAgent.value) controller.assign(selectedAgent.value.id, agentsList.value, currentUser.value?.id);
};
const refreshVisible = () => {
  if (document.visibilityState === 'visible' && view.value.record?.supported !== false && !view.value.loading) controller.refresh();
};
let timer;
onMounted(() => {
  timer = setInterval(refreshVisible, 10000);
  window.addEventListener('focus', refreshVisible);
  document.addEventListener('visibilitychange', refreshVisible);
});
onBeforeUnmount(() => {
  agentGeneration += 1;
  controller.dispose();
  clearInterval(timer);
  window.removeEventListener('focus', refreshVisible);
  document.removeEventListener('visibilitychange', refreshVisible);
});
</script>

<template>
  <section
    v-if="supported"
    class="miniapp-ownership mx-2 mb-2 rounded-lg border border-n-weak bg-n-solid-2 text-n-slate-12"
    :aria-label="t(key('TITLE'))"
    :aria-busy="view.busy || view.loading"
  >
    <div class="miniapp-ownership__row">
      <span class="miniapp-ownership__status" role="status">{{ statusText }}</span>
      <div class="miniapp-ownership__actions">
        <button
          v-if="view.record.can_take_over"
          type="button" class="miniapp-ownership__button miniapp-ownership__button--primary"
          :disabled="disabled" :title="view.record.take_over_problem || ''"
          @click="controller.act('takeover')"
        >{{ t(key('TAKE')) }}</button>
        <button
          v-if="view.record.state === 'staff' || view.record.state === 'waiting'"
          type="button" class="miniapp-ownership__button"
          :disabled="disabled || !view.record.can_return_to_alexey" :title="view.record.return_problem || ''"
          @click="controller.act('return')"
        >{{ t(key('RETURN')) }}</button>
        <button
          v-if="(view.record.assignable_agent_ids || []).some(id => id !== currentUser?.id)"
          type="button" class="miniapp-ownership__button"
          :disabled="disabled" :aria-expanded="choosingAgent"
          @click="openAgentChooser"
        >{{ t(key('OTHER')) }}</button>
      </div>
    </div>
    <p
      v-if="(view.record.state === 'staff' || view.record.state === 'waiting') && !view.record.can_return_to_alexey"
      class="miniapp-ownership__hint" role="status"
    >{{ view.record.return_problem || t(key('RETURN_UNAVAILABLE')) }}</p>
    <p v-if="view.record.state === 'closed'" class="miniapp-ownership__hint">{{ t(key('CLOSED_HINT')) }}</p>
    <p v-if="errorText" class="miniapp-ownership__error" role="alert">{{ errorText }}</p>
    <div v-if="errorText" class="miniapp-ownership__actions">
      <button v-if="view.retryable" type="button" class="miniapp-ownership__button" :disabled="view.busy || view.loading" @click="controller.retry()">{{ t(key('RETRY')) }}</button>
      <button type="button" class="miniapp-ownership__button" :disabled="view.busy || view.loading" @click="controller.refresh()">{{ t(key('REFRESH')) }}</button>
    </div>
    <div v-if="choosingAgent" class="miniapp-ownership__chooser" @keyup.esc="choosingAgent = false">
      <p class="miniapp-ownership__hint">{{ t(key('CHOOSE_HINT')) }}</p>
      <p v-if="agentsLoading" role="status" class="miniapp-ownership__hint">{{ t(key('AGENTS_LOADING')) }}</p>
      <p v-else-if="agentsError" role="alert" class="miniapp-ownership__error">{{ t(key('AGENTS_ERROR')) }}</p>
      <template v-else>
        <MultiselectDropdown
          v-if="assignmentAllowed"
          :options="eligibleAgents" :selected-item="selectedAgent"
          :multiselector-title="t(key('OTHER'))"
          :multiselector-placeholder="t(key('CHOOSE'))"
          :no-search-result="t(key('NO_AGENTS'))"
          :input-placeholder="t(key('SEARCH'))"
          @select="chooseAgent"
        />
        <p v-else class="miniapp-ownership__hint">{{ t(key('NO_AGENTS')) }}</p>
      </template>
      <div class="miniapp-ownership__actions">
        <button type="button" class="miniapp-ownership__button miniapp-ownership__button--primary" :disabled="disabled || !selectedAgent || agentsLoading" @click="assignSelected">{{ t(key('ASSIGN')) }}</button>
        <button type="button" class="miniapp-ownership__button" :disabled="view.busy" @click="choosingAgent = false">{{ t(key('CANCEL')) }}</button>
      </div>
    </div>
  </section>
</template>

<style scoped>
.miniapp-ownership { padding: 10px 12px; font-size: 12px; line-height: 1.45; }
.miniapp-ownership__row { display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 8px 12px; }
.miniapp-ownership__status { font-weight: 600; }
.miniapp-ownership__actions { display: flex; align-items: center; flex-wrap: wrap; gap: 6px; }
.miniapp-ownership__button { min-height: 32px; padding: 5px 9px; border: 1px solid currentColor; border-radius: 6px; background: transparent; color: inherit; font: inherit; font-weight: 500; cursor: pointer; }
.miniapp-ownership__button--primary { background: #2563eb; border-color: #2563eb; color: #fff; }
.miniapp-ownership__button:hover:not(:disabled) { filter: brightness(1.13); }
.miniapp-ownership__button:focus-visible { outline: 2px solid #60a5fa; outline-offset: 2px; }
.miniapp-ownership__button:disabled { opacity: .5; cursor: default; }
.miniapp-ownership__hint { margin: 7px 0 0; opacity: .8; }
.miniapp-ownership__error { margin: 7px 0; color: #e57373; }
.miniapp-ownership__chooser { max-width: 360px; margin-top: 10px; padding-top: 8px; border-top: 1px solid currentColor; }
.miniapp-ownership__chooser .miniapp-ownership__hint { margin: 0 0 8px; }
@media (max-width: 600px) { .miniapp-ownership__button { min-height: 40px; } }
</style>
